import {
  type OnboardingDraftV1,
  type OnboardingInterest,
  type OnboardingRecommendationAnswers,
  type OnboardingSelfDescription,
  type UsernameAvailability,
} from "@repo/types";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft01Icon, X } from "~/components/icons/central-icons";
import { Button } from "~/components/ui/button";
import { fluidSprings } from "~/components/ui/fluid-motion";
import { ProgressSteps } from "~/components/ui/progress";
import { type OnboardingV1DraftStorage } from "~/features/start/data/onboarding-v1-draft";
import {
  getOnboardingV1StepIndex,
  type OnboardingV1StepId,
} from "~/features/start/data/onboarding-v1-step-search";
import {
  type OnboardingV1RecommendationDataSource,
  usePrefetchOnboardingV1Recommendations,
} from "~/features/start/state/use-onboarding-v1-recommendations";
import {
  type OnboardingV1AccountDataSource,
  type OnboardingV1ClaimedDraft,
} from "~/features/start/state/onboarding-v1-account-data-source";
import {
  useOnboardingV1PageController,
  type OnboardingV1QuestionState,
} from "~/features/start/state/use-onboarding-v1-page-controller";
import type { OnboardingV1UsernameIdentity } from "~/features/start/state/use-onboarding-v1-username-identity";
import { getPublicAssetUrl } from "~/lib/env";
import { m as messages } from "~/lib/i18n";
import { cn } from "~/lib/utils";
import {
  OnboardingV1InterestsStep,
  OnboardingV1NameStep,
  OnboardingV1ProfileStep,
  type UsernameCheckState,
} from "./onboarding-v1-question-steps";
import { OnboardingV1AccountStep } from "./onboarding-v1-account-step";
import { OnboardingV1PickStep } from "./onboarding-v1-pick-step";
import { OnboardingV1ProjectionStep } from "./onboarding-v1-projection-step";
import { OnboardingV1FundStep } from "./onboarding-v1-fund-step";
import { OnboardingV1RetryState } from "./onboarding-v1-retry-state";
import "./onboarding-v1-page.css";

type PressMarkId =
  | "american-banker"
  | "forbes"
  | "fortune"
  | "new-york-post"
  | "reuters"
  | "schwab-network"
  | "yahoo-finance";

interface StandardPressMark {
  readonly height: number;
  readonly id: Exclude<PressMarkId, "yahoo-finance">;
  readonly kind: "standard";
  readonly path: string;
  readonly width: number;
}

interface YahooPressMark {
  readonly height: number;
  readonly id: "yahoo-finance";
  readonly kind: "yahoo";
  readonly width: number;
}

type PressMark = StandardPressMark | YahooPressMark;

interface OnboardingV1PageProps {
  readonly accountDataSource?: OnboardingV1AccountDataSource;
  readonly checkUsernameAvailability?: (
    username: string,
  ) => Promise<UsernameAvailability>;
  readonly onAuthenticatedContinue: () => void;
  readonly recommendationDataSource?: OnboardingV1RecommendationDataSource;
  readonly onClose: () => void;
  readonly onCompleted: () => void;
  readonly onExplore: () => void;
  readonly onReturningUserLogin: () => void;
  readonly onStepChange: (step: OnboardingV1StepId) => void;
  readonly draftStorage?: OnboardingV1DraftStorage | null;
  readonly step: OnboardingV1StepId;
  readonly usernameIdentity: OnboardingV1UsernameIdentity;
}

type ReturningUserActionMode = "continue" | "hidden" | "login";

const BACKGROUND_WIDTHS = [640, 1024, 1662] as const;
const QUESTION_STEP_IDS = ["name", "profile", "interests", "pick"] as const;

const PRESS_MARKS: readonly PressMark[] = [
  {
    height: 35,
    id: "reuters",
    kind: "standard",
    path: "/landing/press/reuters.svg",
    width: 125,
  },
  {
    height: 97,
    id: "fortune",
    kind: "standard",
    path: "/landing/press/fortune.svg",
    width: 382,
  },
  {
    height: 50,
    id: "forbes",
    kind: "standard",
    path: "/landing/press/forbes.svg",
    width: 198,
  },
  {
    height: 40,
    id: "american-banker",
    kind: "standard",
    path: "/landing/press/american-banker.svg",
    width: 275,
  },
  {
    height: 34,
    id: "schwab-network",
    kind: "standard",
    path: "/landing/press/schwab-network.svg",
    width: 101,
  },
  {
    height: 21,
    id: "yahoo-finance",
    kind: "yahoo",
    width: 148,
  },
  {
    height: 178,
    id: "new-york-post",
    kind: "standard",
    path: "/onboarding-v1/new-york-post.webp",
    width: 1178,
  },
];

const desktopSceneVariants = {
  center: {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  enter: (direction: number) => ({
    opacity: 0,
    transform: `translate3d(calc(var(--onboarding-v1-motion-distance) * ${direction}), 0, 0)`,
  }),
  exit: (direction: number) => ({
    opacity: 0,
    transform: `translate3d(calc(var(--onboarding-v1-motion-distance) * ${-direction}), 0, 0)`,
    transition: fluidSprings.slow.exit,
  }),
  reducedCenter: {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  reducedEnter: {
    opacity: 0,
    transform: "translate3d(0, 0, 0)",
  },
  reducedExit: {
    opacity: 0,
    transform: "translate3d(0, 0, 0)",
    transition: fluidSprings.fast.exit,
  },
};

function getBackgroundSrcSet(format: "avif" | "webp"): string {
  return BACKGROUND_WIDTHS.map(
    (width) =>
      `${getPublicAssetUrl(`/onboarding-v1/hook-background-${width}.${format}`)} ${width}w`,
  ).join(", ");
}

function getPressMarkName(id: PressMarkId): string {
  switch (id) {
    case "american-banker":
      return messages.onboarding_v1_press_american_banker();
    case "forbes":
      return messages.onboarding_v1_press_forbes();
    case "fortune":
      return messages.onboarding_v1_press_fortune();
    case "new-york-post":
      return messages.onboarding_v1_press_new_york_post();
    case "reuters":
      return messages.onboarding_v1_press_reuters();
    case "schwab-network":
      return messages.onboarding_v1_press_schwab_network();
    case "yahoo-finance":
      return messages.onboarding_v1_press_yahoo_finance();
  }
}

function OnboardingBackground(props: { readonly step: OnboardingV1StepId }) {
  return (
    <div
      aria-hidden="true"
      className="onboarding-v1__background"
      data-variant={props.step === "hook" ? "hook" : "flow"}
    >
      <picture className="onboarding-v1__background-picture">
        <source
          sizes="100vw"
          srcSet={getBackgroundSrcSet("avif")}
          type="image/avif"
        />
        <img
          alt=""
          className="onboarding-v1__background-image"
          decoding="async"
          draggable={false}
          fetchPriority="high"
          height={1328}
          sizes="100vw"
          src={getPublicAssetUrl("/onboarding-v1/hook-background-1662.webp")}
          srcSet={getBackgroundSrcSet("webp")}
          width={1662}
        />
      </picture>
      <div className="onboarding-v1__background-wash" />
    </div>
  );
}

function YahooFinanceMark() {
  return (
    <span aria-hidden="true" className="onboarding-v1__yahoo-mark">
      <img
        alt=""
        className="onboarding-v1__yahoo-wordmark"
        height={21}
        src={getPublicAssetUrl("/onboarding-v1/yahoo-wordmark.svg")}
        width={75}
      />
      <img
        alt=""
        className="onboarding-v1__yahoo-finance"
        height={17}
        src={getPublicAssetUrl("/onboarding-v1/yahoo-finance.svg")}
        width={73}
      />
    </span>
  );
}

function PressMarkItem(props: { readonly mark: PressMark }) {
  const publication = getPressMarkName(props.mark.id);

  return (
    <li className="onboarding-v1__press-mark-item">
      <span
        aria-label={messages.onboarding_v1_press_mark_label({ publication })}
        className={cn(
          "onboarding-v1__press-mark",
          `onboarding-v1__press-mark--${props.mark.id}`,
        )}
        role="img"
      >
        {props.mark.kind === "yahoo" ? (
          <YahooFinanceMark />
        ) : (
          <img
            alt=""
            aria-hidden="true"
            height={props.mark.height}
            src={getPublicAssetUrl(props.mark.path)}
            width={props.mark.width}
          />
        )}
      </span>
    </li>
  );
}

function HookStep(props: {
  readonly onContinue: () => void;
  readonly onSecondaryAction: () => void;
  readonly returningUserActionMode: ReturningUserActionMode;
}) {
  return (
    <div className="onboarding-v1__hook">
      <header className="onboarding-v1__hook-header">
        <img
          alt={messages.flagship_onboarding_glider_home_label()}
          className="onboarding-v1__wordmark"
          height={16}
          src={getPublicAssetUrl("/landing/brand-horizontal.svg")}
          width={126}
        />
      </header>

      <div className="onboarding-v1__hook-content">
        <div className="onboarding-v1__hook-copy">
          <h1>{messages.onboarding_v1_hook_heading()}</h1>
          <p>{messages.onboarding_v1_hook_description()}</p>
          <div className="onboarding-v1__hook-actions">
            <Button
              className="onboarding-v1__hook-primary"
              onClick={props.onContinue}
              size="lg"
              type="button"
              variant="inverse"
            >
              {messages.onboarding_v1_hook_primary_action()}
            </Button>
            {props.returningUserActionMode === "hidden" ? null : (
              <Button
                className="onboarding-v1__hook-login"
                onClick={props.onSecondaryAction}
                type="button"
                variant="link"
              >
                {props.returningUserActionMode === "continue"
                  ? messages.onboarding_v1_hook_authenticated_action()
                  : messages.onboarding_v1_hook_returning_user_action()}
              </Button>
            )}
          </div>
        </div>

        <div className="onboarding-v1__press-strip">
          <p>{messages.onboarding_v1_press_intro()}</p>
          <ul>
            {PRESS_MARKS.map((mark) => (
              <PressMarkItem key={mark.id} mark={mark} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function EmptyStep(props: { readonly step: OnboardingV1StepId }) {
  return (
    <div
      aria-hidden="true"
      className="onboarding-v1__empty-step"
      data-onboarding-step={props.step}
      data-testid={`onboarding-v1-step-${props.step}`}
    />
  );
}

function OnboardingV1RecommendationPrefetch(props: {
  readonly answers: OnboardingRecommendationAnswers;
  readonly dataSource?: OnboardingV1RecommendationDataSource;
}) {
  usePrefetchOnboardingV1Recommendations({
    answers: props.answers,
    dataSource: props.dataSource,
  });
  return null;
}

function StepContent(props: {
  readonly accountDataSource: OnboardingV1AccountDataSource;
  readonly allowAutomaticCreate: boolean;
  readonly claimedDraft: OnboardingV1ClaimedDraft | null;
  readonly draft: OnboardingDraftV1 | null;
  readonly onFundingCreatingChange: (isCreating: boolean) => void;
  readonly onAccountClaimed: (draft: OnboardingV1ClaimedDraft) => void;
  readonly onAuthenticatedContinue: () => void;
  readonly onClaimedDraftChange: (draft: OnboardingV1ClaimedDraft) => void;
  readonly onCompleted: () => void;
  readonly onInterestToggle: (value: OnboardingInterest) => void;
  readonly onInterestsUnknownToggle: () => void;
  readonly onProfileToggle: (value: OnboardingSelfDescription) => void;
  readonly onUsernameChange: (value: string) => void;
  readonly onUsernameContinue: () => void;
  readonly onUsernameConflict: () => void;
  readonly onContinue: () => void;
  readonly onExplore: () => void;
  readonly onPickSelect: (blueprintId: string) => void;
  readonly onReturningUserLogin: () => void;
  readonly onStarterInvalid: () => void;
  readonly onClaimedStarterInvalid: () => Promise<void>;
  readonly questionState: OnboardingV1QuestionState;
  readonly recommendationAnswers: OnboardingRecommendationAnswers;
  readonly recommendationDataSource?: OnboardingV1RecommendationDataSource;
  readonly returningUserActionMode: ReturningUserActionMode;
  readonly selectedBlueprintId: string | null;
  readonly step: OnboardingV1StepId;
  readonly usernameCheckState: UsernameCheckState;
}) {
  switch (props.step) {
    case "hook":
      return (
        <HookStep
          onContinue={props.onContinue}
          onSecondaryAction={
            props.returningUserActionMode === "continue"
              ? props.onAuthenticatedContinue
              : props.onReturningUserLogin
          }
          returningUserActionMode={props.returningUserActionMode}
        />
      );
    case "name":
      return (
        <OnboardingV1NameStep
          checkState={props.usernameCheckState}
          onChange={props.onUsernameChange}
          onContinue={props.onUsernameContinue}
          onReturningUserLogin={props.onReturningUserLogin}
          showReturningUserLogin={props.returningUserActionMode === "login"}
          username={props.questionState.username}
        />
      );
    case "profile":
      return (
        <OnboardingV1ProfileStep
          onContinue={() => props.onContinue()}
          onToggle={props.onProfileToggle}
          selected={props.questionState.selfDescriptions}
        />
      );
    case "interests":
      return (
        <OnboardingV1InterestsStep
          interestsUnknown={props.questionState.interestsUnknown}
          onContinue={props.onContinue}
          onToggle={props.onInterestToggle}
          onToggleUnknown={props.onInterestsUnknownToggle}
          selected={props.questionState.interests}
          username={props.questionState.username}
        />
      );
    case "projection":
      return (
        <>
          <OnboardingV1RecommendationPrefetch
            answers={props.recommendationAnswers}
            dataSource={props.recommendationDataSource}
          />
          <OnboardingV1ProjectionStep onContinue={props.onContinue} />
        </>
      );
    case "pick":
      return (
        <OnboardingV1PickStep
          answers={props.recommendationAnswers}
          dataSource={props.recommendationDataSource}
          onContinue={props.onContinue}
          onExplore={props.onExplore}
          onSelect={props.onPickSelect}
          selectedBlueprintId={props.selectedBlueprintId}
        />
      );
    case "account":
      return props.draft ? (
        <OnboardingV1AccountStep
          dataSource={props.accountDataSource}
          draft={props.draft}
          onClaimed={props.onAccountClaimed}
          onStarterInvalid={props.onStarterInvalid}
          onUsernameConflict={props.onUsernameConflict}
        />
      ) : (
        <EmptyStep step="account" />
      );
    case "fund":
      return props.claimedDraft ? (
        <OnboardingV1FundStep
          allowAutomaticCreate={props.allowAutomaticCreate}
          dataSource={props.accountDataSource}
          draft={props.claimedDraft}
          onCompleted={props.onCompleted}
          onCreatingChange={props.onFundingCreatingChange}
          onDraftChange={props.onClaimedDraftChange}
          onStarterInvalid={props.onClaimedStarterInvalid}
          recommendationDataSource={props.recommendationDataSource}
        />
      ) : (
        <EmptyStep step="fund" />
      );
  }
}

function getCompletedQuestionCount(step: OnboardingV1StepId): number {
  switch (step) {
    case "hook":
      return 0;
    case "name":
      return 1;
    case "profile":
      return 2;
    case "interests":
    case "projection":
      return 3;
    case "pick":
    case "account":
    case "fund":
      return 4;
  }
}

function getDraftCompletedQuestionCount(
  draft: OnboardingDraftV1 | null,
): number {
  if (!draft?.username) {
    return 0;
  }
  if (draft.selfDescriptions.length === 0) {
    return 1;
  }
  if (!draft.interestsUnknown && draft.interests.length === 0) {
    return 2;
  }
  if (!draft.selectedBlueprintId) {
    return 3;
  }
  return 4;
}

function getMobileBackStep(
  step: Exclude<OnboardingV1StepId, "hook">,
  draftCompletedQuestionCount: number,
  disabled: boolean,
): OnboardingV1StepId | null {
  if (disabled) {
    return null;
  }

  switch (step) {
    case "interests":
      return draftCompletedQuestionCount >= 2 ? "profile" : null;
    case "projection":
      return draftCompletedQuestionCount >= 3 ? "interests" : null;
    case "pick":
      return draftCompletedQuestionCount >= 3 ? "projection" : null;
    case "account":
      return draftCompletedQuestionCount >= 4 ? "pick" : null;
    case "name":
    case "profile":
    case "fund":
      return null;
  }
}

function FlowHeader(props: {
  readonly disabled?: boolean;
  readonly draft: OnboardingDraftV1 | null;
  readonly onClose: () => void;
  readonly onStepChange: (step: OnboardingV1StepId) => void;
  readonly step: Exclude<OnboardingV1StepId, "hook">;
}) {
  const draftCompletedQuestionCount = getDraftCompletedQuestionCount(
    props.draft,
  );
  const completedQuestionCount = Math.max(
    getCompletedQuestionCount(props.step),
    draftCompletedQuestionCount,
  );
  const canNavigate =
    !props.disabled && props.step !== "fund" && draftCompletedQuestionCount > 1;
  const mobileBackStep = getMobileBackStep(
    props.step,
    draftCompletedQuestionCount,
    props.disabled ?? false,
  );
  const progressProps = {
    "aria-label": messages.flagship_onboarding_progress_label(),
    className: "onboarding-v1__progress",
    max: QUESTION_STEP_IDS.length,
    value: completedQuestionCount,
  } as const;

  return (
    <header className="onboarding-v1__flow-header">
      {mobileBackStep ? (
        <Button
          aria-label={messages.flagship_onboarding_back_label()}
          className="onboarding-v1__back"
          onClick={() => props.onStepChange(mobileBackStep)}
          size="icon-lg"
          type="button"
          variant="ghost"
        >
          <span
            aria-hidden="true"
            className="onboarding-v1__header-control-visual"
          >
            <ArrowLeft01Icon className="onboarding-v1__header-control-icon" />
          </span>
        </Button>
      ) : null}
      <img
        alt={messages.flagship_onboarding_glider_home_label()}
        className="onboarding-v1__wordmark"
        height={24}
        src={getPublicAssetUrl("/landing/brand-horizontal.svg")}
        width={189}
      />
      {canNavigate ? (
        <ProgressSteps
          {...progressProps}
          getStepLabel={(index) =>
            messages.flagship_onboarding_step_count({
              step: index + 1,
              total: QUESTION_STEP_IDS.length,
            })
          }
          isStepSelectable={(index) =>
            index > 0 && index < draftCompletedQuestionCount
          }
          onStepSelect={(index) => {
            const targetStep = QUESTION_STEP_IDS[index];
            if (targetStep) {
              props.onStepChange(targetStep);
            }
          }}
        />
      ) : (
        <ProgressSteps {...progressProps} />
      )}
      <Button
        aria-label={messages.onboarding_v1_close_action()}
        className="onboarding-v1__close"
        disabled={props.disabled}
        onClick={props.onClose}
        size="icon-lg"
        type="button"
        variant="ghost"
      >
        <span
          aria-hidden="true"
          className="onboarding-v1__header-control-visual"
        >
          <X className="onboarding-v1__header-control-icon" />
        </span>
      </Button>
    </header>
  );
}

export function OnboardingV1Page(props: OnboardingV1PageProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const previousStepRef = useRef(props.step);
  const [fundingIsCreating, setFundingIsCreating] = useState(false);
  const controller = useOnboardingV1PageController({
    accountDataSource: props.accountDataSource,
    checkUsernameAvailability: props.checkUsernameAvailability,
    draftStorage: props.draftStorage,
    onClose: props.onClose,
    onExplore: props.onExplore,
    onStepChange: props.onStepChange,
    step: props.step,
    usernameIdentity: props.usernameIdentity,
  });
  const currentStepIndex = getOnboardingV1StepIndex(props.step);
  const previousStepIndex = getOnboardingV1StepIndex(previousStepRef.current);
  const direction = currentStepIndex >= previousStepIndex ? 1 : -1;
  const recommendationAnswers = useMemo<OnboardingRecommendationAnswers>(
    () => ({
      interests: [...controller.questionState.interests],
      interestsUnknown: controller.questionState.interestsUnknown,
      selfDescriptions: [...controller.questionState.selfDescriptions],
    }),
    [
      controller.questionState.interests,
      controller.questionState.interestsUnknown,
      controller.questionState.selfDescriptions,
    ],
  );
  const returningUserActionMode: ReturningUserActionMode = props
    .usernameIdentity.authReady
    ? props.usernameIdentity.authenticated
      ? "continue"
      : "login"
    : "hidden";

  useEffect(() => {
    previousStepRef.current = props.step;
  }, [props.step]);

  return (
    <main
      className="onboarding-v1"
      data-onboarding-step={props.step}
      data-testid="onboarding-v1-page"
    >
      <OnboardingBackground step={props.step} />
      {props.step === "hook" ? null : (
        <FlowHeader
          disabled={controller.isClosing || fundingIsCreating}
          draft={controller.draft}
          onClose={() => void controller.handleClose()}
          onStepChange={props.onStepChange}
          step={props.step}
        />
      )}

      <div className="onboarding-v1__stage">
        <AnimatePresence custom={direction} initial={false} mode="sync">
          <m.section
            animate={reduceMotion ? "reducedCenter" : "center"}
            className="onboarding-v1__scene"
            custom={direction}
            exit={reduceMotion ? "reducedExit" : "exit"}
            initial={reduceMotion ? "reducedEnter" : "enter"}
            key={props.step}
            transition={reduceMotion ? fluidSprings.fast : fluidSprings.slow}
            variants={desktopSceneVariants}
          >
            {controller.pageError ? (
              <OnboardingV1RetryState
                className="onboarding-v1__page-error"
                onRetry={controller.retryPageAction}
              />
            ) : props.step === "name" && controller.shouldHideUsernameStep ? (
              <EmptyStep step="name" />
            ) : (
              <StepContent
                accountDataSource={controller.accountDataSource}
                allowAutomaticCreate={controller.claimedInCurrentSession}
                claimedDraft={controller.claimedDraft}
                draft={controller.draft}
                onFundingCreatingChange={setFundingIsCreating}
                onAccountClaimed={controller.handleClaimed}
                onAuthenticatedContinue={props.onAuthenticatedContinue}
                onClaimedDraftChange={controller.setClaimedDraft}
                onClaimedStarterInvalid={controller.handleClaimedStarterInvalid}
                onCompleted={props.onCompleted}
                onContinue={controller.handleContinue}
                onInterestToggle={controller.handleInterestToggle}
                onInterestsUnknownToggle={
                  controller.handleInterestsUnknownToggle
                }
                onExplore={controller.handleExplore}
                onPickSelect={controller.handlePickSelect}
                onProfileToggle={controller.handleProfileToggle}
                onReturningUserLogin={props.onReturningUserLogin}
                onStarterInvalid={controller.handleStarterInvalid}
                onUsernameChange={controller.handleUsernameChange}
                onUsernameContinue={() => {
                  void controller.handleUsernameContinue();
                }}
                onUsernameConflict={controller.handleUsernameConflict}
                questionState={controller.questionState}
                recommendationAnswers={recommendationAnswers}
                recommendationDataSource={props.recommendationDataSource}
                returningUserActionMode={returningUserActionMode}
                selectedBlueprintId={controller.selectedBlueprintId}
                step={props.step}
                usernameCheckState={controller.usernameCheckState}
              />
            )}
          </m.section>
        </AnimatePresence>
      </div>
    </main>
  );
}
